import { capitalize, get, isEmpty, map, pickBy, sumBy, values } from "lodash";
import moment from "moment";
import React, { useMemo } from "react";
import AlertMessage from "../../../../components/AlertMessage";
import CenteredLoading from "../../../../components/CenteredLoading";
import DonutChart, {
  DonutSection
} from "../../../../components/charts/DonutChart";
import ExternalLink from "../../../../components/ExternalLink";
import {
  AllWitnessesData,
  useHotspots
} from "../../../../context/hotspots/HotspotsProvider";
import { useTransmitScales } from "../../../../context/transmitScales";
import {
  classificationColorMap,
  formatHotspotName,
  getClassification
} from "../../../../utils/hotspotUtils";
import { Hotspot } from "../../../../utils/types";
import {
  WitnessesVisualizationKey,
  WitnessesVisualizationVariant
} from "../../../../utils/useMapConfig";

interface Props {
  hotspot: Hotspot;
}

const WitnessedTransmitScalesDonutChart: React.FC<Props> = ({ hotspot }) => {
  const { hotspotsMap, hotspotWitnessesDataMap } = useHotspots();
  const { calculatedTransmitScales, isTransmitScaleActive } =
    useTransmitScales();

  const hotspotName = useMemo(() => formatHotspotName(hotspot), [hotspot]);

  const allWitnessedOthersData = useMemo(
    () =>
      get(
        hotspotWitnessesDataMap,
        [
          hotspot.id,
          WitnessesVisualizationKey.witnessedOthers,
          WitnessesVisualizationVariant.all
        ],
        []
      ) as AllWitnessesData[],
    [hotspotWitnessesDataMap, hotspot.id]
  );

  const witnessedTransmitScalesMap = useMemo(
    () =>
      allWitnessedOthersData.reduce(
        (acc, { id, validCount, invalidCount, invalidReasons }) => {
          const beaconingHotspot = hotspotsMap[id];

          if (
            !beaconingHotspot || // TODO: ensure that they are all fetched
            !isTransmitScaleActive(id)
          ) {
            return acc;
          }

          const rewardScaleClassification = getClassification(
            calculatedTransmitScales[id] || 0
          );

          const { hotspots, validWitnesses, invalidWitnesses } =
            acc[rewardScaleClassification];

          return {
            ...acc,
            [rewardScaleClassification]: {
              hotspots: hotspots + 1,
              validWitnesses: validWitnesses + validCount,
              invalidWitnesses: invalidWitnesses + invalidCount
            }
          };
        },
        {
          excellent: { hotspots: 0, validWitnesses: 0, invalidWitnesses: 0 },
          good: { hotspots: 0, validWitnesses: 0, invalidWitnesses: 0 },
          mediocre: { hotspots: 0, validWitnesses: 0, invalidWitnesses: 0 },
          poor: { hotspots: 0, validWitnesses: 0, invalidWitnesses: 0 },
          terrible: { hotspots: 0, validWitnesses: 0, invalidWitnesses: 0 },
          inactive: { hotspots: 0, validWitnesses: 0, invalidWitnesses: 0 }
        }
      ),
    [
      allWitnessedOthersData,
      calculatedTransmitScales,
      isTransmitScaleActive,
      hotspotsMap
    ]
  );

  const witnessedOthersByTransmitScaleChartData: DonutSection[] = useMemo(
    () =>
      map(
        pickBy(witnessedTransmitScalesMap, ({ hotspots }) => hotspots > 0),
        ({ hotspots, validWitnesses, invalidWitnesses }, key: string) => ({
          key,
          name: capitalize(key),
          value: hotspots,
          displayValues: [
            `${hotspots} hotspots`,
            `${validWitnesses} valid beacons`,
            `${invalidWitnesses} invalid beacons`
          ],
          percentage:
            (hotspots * 100) /
            sumBy(values(witnessedTransmitScalesMap), "hotspots"),
          color: get(classificationColorMap, key)
        })
      ),
    [witnessedTransmitScalesMap]
  );

  const alertMessage = useMemo(() => {
    let hotspotsWithPerfectTransmitScale = 0;
    let hotspotsBelowPerfectTransmitScale = 0;

    allWitnessedOthersData.forEach(({ id }) => {
      const transmitScale = calculatedTransmitScales[id] || 0;
      if (transmitScale === 1) {
        hotspotsWithPerfectTransmitScale++;
      } else {
        hotspotsBelowPerfectTransmitScale++;
      }
    });

    if (isEmpty(allWitnessedOthersData)) return undefined;

    if (hotspotsWithPerfectTransmitScale === allWitnessedOthersData.length) {
      return `All ${hotspotsWithPerfectTransmitScale} hotspots witnessed by ${hotspotName} have a perfect transmit scale of 1, which is fantastic! This means that none of the rewards for witnessing are reduced due to reward scaling.`;
    }

    return `Out of ${allWitnessedOthersData.length} hotspots witnessed by ${hotspotName}, only ${hotspotsWithPerfectTransmitScale} have a perfect transmit scale of 1. This means that the rewards for witnessing ${hotspotsBelowPerfectTransmitScale} hotspots are reduced. ${hotspotName} can earn more if the transmit scales for these hotspots are improved.`;
  }, [hotspotName, allWitnessedOthersData, calculatedTransmitScales]);

  return (
    <div>
      <dt className="text-sm font-medium text-gray-500 dark:text-gray-300">
        <ExternalLink
          iconStyle={{ width: "1rem", height: "1rem" }}
          text="Witnessed transmit scales"
          url="https://docs.helium.com/blockchain/proof-of-coverage/#poc-reward-scaling"
        />
      </dt>

      <dd className="mt-4">
        {isEmpty(get(hotspotWitnessesDataMap, hotspot.id)) ? (
          <CenteredLoading className="h-60" />
        ) : isEmpty(witnessedOthersByTransmitScaleChartData) ? (
          <span className="text-sm text-gray-600 dark:text-gray-300">
            {hotspotName} didn't witness any beacons{" "}
            {hotspot.last_assertion &&
            moment()
              .utc()
              .subtract(7, "days")
              .isBefore(moment.unix(hotspot.last_assertion).utc().utc())
              ? `since its last assertion ${moment
                  .unix(hotspot.last_assertion)
                  .fromNow()}.`
              : "over the past 7 days."}
          </span>
        ) : (
          <>
            <DonutChart
              className="mx-auto"
              data={witnessedOthersByTransmitScaleChartData}
              sizeInPx={240}
            />
            {alertMessage && (
              <AlertMessage
                className="mt-4"
                type="info"
                title="Witnessed transmit scales"
                message={alertMessage}
                collapsible
              />
            )}
          </>
        )}
      </dd>
    </div>
  );
};

export default WitnessedTransmitScalesDonutChart;
